/**
 * Program affiliation of a business (Business Account Pages slice 2, migration 2026-10-06-315).
 * Display/record only: NOT a coverage flag and NOT membership. Nothing here touches members, subscriptions,
 * households, check-ins or journeys.
 */

import { supabase } from "./supabase";
import type { UUID } from "../types/common.types";

export const PROGRAMS = [{ value: "sheepdog", label: "Sheepdog" }] as const;
export type ProgramKey = (typeof PROGRAMS)[number]["value"];

export const AFFILIATION_STATUSES = [
  {
    value: "proposed",
    label: "Proposed",
    hint: "In discussion; not agreed yet",
  },
  {
    value: "confirmed",
    label: "Confirmed",
    hint: "The business has agreed to participate",
  },
  {
    value: "ended",
    label: "Ended",
    hint: "No longer participating (kept for history)",
  },
] as const;
export type AffiliationStatus = (typeof AFFILIATION_STATUSES)[number]["value"];

export const AFFILIATION_SOURCES = [
  { value: "agreement", label: "Signed agreement" },
  { value: "email", label: "Email" },
  { value: "meeting", label: "Meeting / call" },
  { value: "referral", label: "Bus Dev referral" },
  { value: "other", label: "Other" },
] as const;
export type AffiliationSource = (typeof AFFILIATION_SOURCES)[number]["value"];

export interface ProgramAffiliation {
  id: UUID;
  organization_id: UUID;
  program: ProgramKey;
  status: AffiliationStatus;
  source: AffiliationSource;
  reference: string | null;
  notes: string | null;
  proposed_at: string;
  confirmed_at: string | null;
  ended_at: string | null;
  status_changed_at: string;
  updated_at: string;
}

export const programLabel = (p: string): string =>
  PROGRAMS.find((x) => x.value === p)?.label ?? p;
export const statusLabel = (s: string): string =>
  AFFILIATION_STATUSES.find((x) => x.value === s)?.label ?? s;
export const sourceLabel = (s: string): string =>
  AFFILIATION_SOURCES.find((x) => x.value === s)?.label ?? s;

export const getProgramAffiliations = async (
  organizationId: UUID,
): Promise<ProgramAffiliation[]> => {
  const { data, error } = await supabase
    .from("organization_program_affiliations")
    .select("*")
    .eq("organization_id", organizationId)
    .order("program")
    .limit(20);
  if (error) {
    throw error;
  }
  return (data ?? []) as ProgramAffiliation[];
};

export interface AffiliationInput {
  program: ProgramKey;
  status: AffiliationStatus;
  source: AffiliationSource;
  reference?: string | null;
  notes?: string | null;
}

/** Timestamps (proposed/confirmed/ended) are stamped by the server, never sent from here. */
export const saveProgramAffiliation = async (
  organizationId: UUID,
  input: AffiliationInput,
  existingId?: UUID,
): Promise<void> => {
  const payload = {
    status: input.status,
    source: input.source,
    reference: input.reference?.trim() || null,
    notes: input.notes?.trim() || null,
  };
  const { error } = existingId
    ? await supabase
        .from("organization_program_affiliations")
        .update(payload)
        .eq("id", existingId)
    : await supabase.from("organization_program_affiliations").insert({
        organization_id: organizationId,
        program: input.program,
        ...payload,
      });
  if (error) {
    throw error;
  }
};

export const deleteProgramAffiliation = async (id: UUID): Promise<void> => {
  const { error } = await supabase
    .from("organization_program_affiliations")
    .delete()
    .eq("id", id);
  if (error) {
    throw error;
  }
};
